import React, { useEffect, useLayoutEffect, useState } from "react";
import * as Form from "@radix-ui/react-form";
import Check from "../../../svgs/FilledCheck";
import ClosedEye from "../../../svgs/ClosedEye";
import OpenEye from "../../../svgs/OpenEye";
import { SelectArrow } from "../../../svgs/Arrow";

type InputWrapperType = {
  children: React.ReactNode;
  label: string | React.ReactNode;
  errorMsg?: string;
  hasError?: boolean;
  optional?: boolean;
  name: string;
  valueExists: boolean;
  loading?: boolean;
  valid: boolean;
  passwordInput?: {
    showPassword: boolean;
    handleIconClick: () => void;
  };
  isSelectInput?: boolean;
};

const InputWrapper: React.FC<InputWrapperType> = ({
  children,
  errorMsg,
  hasError,
  label,
  optional,
  name,
  valueExists,
  loading,
  valid,
  passwordInput,
  isSelectInput,
}) => {
  const [shouldBeMoreToTheTop, setShouldBeMoreToTheTop] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [shouldPushDownTheIcon, setShouldPushDownTheIcon] = useState(false);

  useLayoutEffect(() => {
    const label = document.getElementById("floating-label-" + name);
    if (!label) return;
    let computedStyle = window.getComputedStyle(label);
    let computedHeight = parseFloat(computedStyle.height);
    if (computedHeight > 28) setShouldBeMoreToTheTop(true);
  }, []);

  useEffect(() => {
    setShouldPushDownTheIcon(valueExists || isFocused);
  }, [isFocused, valueExists]);

  const pushDownTheIcon = (marginTop: string) =>
    shouldPushDownTheIcon ? marginTop : "mt-0";

  const showErrorMsg = errorMsg && hasError;

  const labelClassName = `absolute left-0 -top-[30px] text-forest-900 text-base transition-all
    peer-placeholder-shown:text-lg peer-placeholder-shown:text-forest-500 peer-placeholder-shown:pb-0 border-transparent border-x-0 
    ${
      shouldBeMoreToTheTop
        ? "peer-placeholder-shown:-top-4"
        : "peer-placeholder-shown:top-2"
    }
    peer-focus:-top-[30px] peer-focus:text-forest-900 peer-focus:text-base
    peer-focus:not-italic peer-focus:font-normal peer-focus:leading-[160%]
    flex justify-between items-start self-stretch w-full
    not-italic font-normal leading-[160%] ${
      isSelectInput ? "cursor-pointer" : "cursor-text"
    }`;

  return (
    <Form.Field
      name={name ?? ""}
      onFocus={() => setIsFocused(true)}
      onBlur={() => setIsFocused(false)}
      className={`relative w-full border-transparent border-0
        ${shouldPushDownTheIcon && "animate-slideUpMargin"} 
        ${pushDownTheIcon("mt-[30px] sm:mt-[34px]")} flex flex-col gap-2 ${
        shouldBeMoreToTheTop && isSelectInput && "mt-4"
      }`}
    >
      {children}
      {optional ? (
        <Form.Label
          id={`floating-label-${name}`}
          className={`${labelClassName}`}
        >
          <span
            style={{ color: "inherit" }}
            className="z-20 pointer-events-none"
          >
            {label}
          </span>
          {isSelectInput ? (
            <div className="flex">
              <span
                className={`flex h-6 px-2 py-1 justify-center items-center gap-2.5 mx-2 z-10
              text-sm font-normal not-italic leading-[165%] text-forest-900 bg-forest-100 rounded-full
              ${
                shouldPushDownTheIcon ? "absolute -right-3" : "relative right-0"
              }`}
              >
                Optional
              </span>
              {valid ? (
                <Check
                  className={`w-5 h-5 fill-mint-200 self-center z-10 ${pushDownTheIcon(
                    "mt-10"
                  )}`}
                />
              ) : (
                <SelectArrow
                  className={`w-[14px] h-[10px] self-center z-10 ${pushDownTheIcon(
                    "mt-11"
                  )}`}
                />
              )}
            </div>
          ) : (
            <span
              className={`flex h-6 px-2 py-1 justify-center items-center gap-2.5 mx-2 z-10
            text-sm font-normal not-italic leading-[165%] text-forest-900 bg-forest-100 rounded-full self-center`}
            >
              Optional
            </span>
          )}
        </Form.Label>
      ) : (
        <Form.Label id={`floating-label-${name}`} className={labelClassName}>
          <span
            style={{ color: "inherit" }}
            className="z-10 pointer-events-none bg-transparent border-transparent border-x-0"
          >
            {label}
          </span>
          {loading && (
            <div
              className={`size-6 animate-spin bg-gradient-to-r from-gray-300 to-transparent rounded-full z-10
            ${pushDownTheIcon("mt-10")}`}
            />
          )}
          {!loading && valid && !passwordInput && (
            <Check
              className={`w-5 h-5 fill-mint-200 self-center z-10 ${pushDownTheIcon(
                "mt-10"
              )}`}
            />
          )}
          {passwordInput && passwordInput.showPassword && (
            <OpenEye
              className={`w-5 h-5 self-center z-10 cursor-pointer ${pushDownTheIcon(
                "mt-10"
              )}`}
              onClick={passwordInput.handleIconClick}
            />
          )}
          {passwordInput && !passwordInput.showPassword && (
            <ClosedEye
              className={`w-5 h-5 self-center z-10 cursor-pointer ${pushDownTheIcon(
                "mt-10"
              )}`}
              onClick={passwordInput.handleIconClick}
            />
          )}
          {isSelectInput && !valid && (
            <SelectArrow
              className={`w-[14px] h-[10px] self-center z-10 ${pushDownTheIcon(
                "mt-10"
              )}`}
            />
          )}
        </Form.Label>
      )}
      {showErrorMsg && (
        <p className="text-clay-700 text-[16px] font-normal not-italic leading-[160%]">
          {errorMsg}
        </p>
      )}
    </Form.Field>
  );
};

export default InputWrapper;
